import React from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StoreContext } from "storeon/react";
import { store } from "./store";
import "./shared/styles/globals.scss";
import "./shared/styles/base.scss";
import "./translations/i18n";
import LiveScoringContainer from "./views/liveScoring/liveScoringContainer";
import LiveScorePlayers from "./views/liveScorePlayers/liveScorePlayers";
import PublicLiveScorePlayers from "./views/liveScorePlayers/publicLiveScorePlayers";
import ThemesURL from "./views/themes/themesURL";
import ScoreCardSettings from "./views/customizeScoreCard/scoreCardSettings";
import TablesSettings from "./views/customizeSummaryTables/tablesSettings";
import ThemesList from "./views/themes/themesList";
import CreateEditTheme from "./views/themes/createEdit/createEditTheme";
import GeneralSettings from "./views/customizeBackground/generalSettings";
import Login from "./views/login/login";
import VerifyAccount from "./views/verifyAccount/verifyAccount";
import withRestriction from "./shared/utils/withRestriction";
import ResetPassword from "./views/login/resetPassword/resetPasswordBox";
import Layout from "./views/components/Layout/Layout";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Profile from "./views/profile/profile";
import Users from "./views/users/usersList";
import { Role } from "./shared/utils/constants/roles";
import { AccountType } from "./shared/utils/constants/accountType";
import CheckConnectivity from "views/components/checkConnectivity";
import routes from "./shared/utils/constants/routes";

// Create a client
const queryClient = new QueryClient();

export default function App() {
  return (
    <StoreContext.Provider value={store}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <ToastContainer />
          <CheckConnectivity />
          <Switch>
            <Route path="/login" component={Login} />
            <Route path="/auth/verify/:code" component={VerifyAccount} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/live-scores" component={PublicLiveScorePlayers} />
            <Layout>
              <Route
                exact
                path="/"
                component={withRestriction({
                  component: <LiveScoringContainer />,
                })}
              />
              <Route
                path="/live-score-players"
                component={withRestriction({
                  component: <LiveScorePlayers />,
                })}
              />

              <Route
                path="/settings-cards"
                component={withRestriction({
                  component: <ScoreCardSettings />,
                  accountType: [AccountType.Premium],
                })}
              />
              <Route
                path="/settings-tables"
                component={withRestriction({
                  component: <TablesSettings />,
                  accountType: [AccountType.Premium],
                })}
              />
              <Route path={routes.Themes.URLS} component={ThemesURL} />
              <Route
                exact
                path={routes.Themes.LIST}
                component={withRestriction({
                  component: <ThemesList />,
                  accountType: [AccountType.Premium],
                  roles: [Role.SuperAdmin, Role.Admin],
                })}
              />
              <Route
                exact
                path={routes.Themes.NEW}
                component={withRestriction({
                  component: <CreateEditTheme />,
                  accountType: [AccountType.Premium],
                  roles: [Role.SuperAdmin, Role.Admin],
                })}
              />
              <Route
                exact
                path={routes.Themes.EDIT}
                component={withRestriction({
                  component: <CreateEditTheme />,
                  accountType: [AccountType.Premium],
                  roles: [Role.SuperAdmin, Role.Admin],
                })}
              />
              <Route
                path="/settings-general"
                component={withRestriction({
                  component: <GeneralSettings />,
                })}
              />
              <Route
                path="/profile"
                component={withRestriction({
                  component: <Profile />,
                })}
              />
              <Route
                path="/users"
                component={withRestriction({
                  component: <Users />,
                  roles: [Role.SuperAdmin, Role.Admin],
                })}
              />
            </Layout>
          </Switch>
        </Router>
      </QueryClientProvider>
    </StoreContext.Provider>
  );
}
